<template>
    <div>
        <van-form @submit="onSubmit">
            <van-field v-model="attendanceDetail.classDate" label="上课日期" readonly @click="showDatePickers"
                placeholder="请选择上课日期" />

            <!-- 弹出日期选择器 -->
            <van-popup v-model:show="showDatePicker" position="bottom">
                <van-date-picker v-model="selectedDate" type="date" @confirm="onConfirmDate"
                    @cancel="showDatePicker = false" />
            </van-popup>

            <van-field v-model="attendanceDetail.startTime" label="上课开始时间" placeholder="请选择开始时间" readonly clickable
                @click="showStartTimePicker = true; startTimeDefault = attendanceDetail.startTime.split(':') || ['12', '00']" />
            <van-popup v-model:show="showStartTimePicker" position="bottom">
                <van-time-picker v-if="showStartTimePicker" v-model="startTimeDefault" @confirm="onStartTimeConfirm"
                    @cancel="showStartTimePicker = false" />
            </van-popup>

            <van-field v-model="attendanceDetail.endTime" label="上课结束时间" placeholder="请选择结束时间" readonly clickable
                @click="showEndTimePicker = true; endTimeDefault = attendanceDetail.endTime.split(':') || ['13', '00']" />
            <van-popup v-model:show="showEndTimePicker" position="bottom">
                <van-time-picker v-if="showEndTimePicker" v-model="endTimeDefault" @confirm="onEndTimeConfirm"
                    @cancel="showEndTimePicker = false" />
            </van-popup>

            <van-field v-model="attendanceDetail.teacherName" label="上课教师" placeholder="请选择上课教师" readonly clickable
                @click="showTeacherPicker = true" />
            <van-popup v-model:show="showTeacherPicker" position="bottom">
                <div style="height: 80vh; overflow-y: auto;">
                    <van-search v-model="teacherSearchQuery" placeholder="搜索老师姓名" show-action
                        @update:model-value="filterTeachers" @cancel="showTeacherPicker = false" />
                    <van-cell v-for="teacher in filteredTeacherList" :key="teacher.userId" :title="teacher.nickName"
                        @click="selectTeacher(teacher)" />
                </div>
            </van-popup>

            <van-field v-model="attendanceDetail.courseName" label="课程" placeholder="请选择课程" readonly clickable
                @click="showCoursePicker = true" />

            <!-- 课程选择弹窗 -->
            <van-popup v-model:show="showCoursePicker" position="bottom">
                <van-picker :columns-field-names="courseFieldName" :columns="courseList" @confirm="onCourseConfirm"
                    @cancel="showCoursePicker = false" />
            </van-popup>

            <van-field label="是否跨校区">
                <template #input>
                    <van-switch v-model="attendanceDetail.isTransCampus" />
                </template>
            </van-field>

            <van-field v-model="attendanceDetail.overClassHour" label="加班课时" placeholder="请输入加班课时数" type="number" />

            <!-- 添加上课内容输入框 -->
            <van-field v-model="attendanceDetail.classContent" label="上课内容" type="textarea" placeholder="请输入上课内容" rows="3" autosize />

            <div style="margin: 16px;">
                <van-button round block type="primary" native-type="submit">
                    保存
                </van-button>
            </div>

            <!-- 加载遮罩 -->
            <van-overlay :show="loading">
                <div class="loading-wrapper">
                    <van-loading size="24px">提交中...</van-loading>
                </div>
            </van-overlay>

            <!-- 排课冲突详情 -->
            <ScheduleConflictPopup v-model:show="conflictVisible" :message="conflictMessage" :data="conflictData" />
        </van-form>
    </div>
</template>


<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { showToast } from 'vant';
import { getAttendanceDetailByPlanIdForH5, getScheduleCourseList, updateSchedulePlan } from '@/api/schedule';
import { getTeacherList } from '@/api/user';
import ScheduleConflictPopup from '@/components/ScheduleConflictPopup.vue';

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const showDatePicker = ref(false);
const schedulePlanId = ref(null);
const courseId = ref(null);
const attendanceDetail = reactive({
    startTime: '',
    endTime: '',
    isTransCampus: false,
    overClassHour: 0,
    classContent: ""
});
const courseFieldName = {
    text: 'courseName',
    value: 'courseId',
};
const showStartTimePicker = ref(false);
const showEndTimePicker = ref(false);
const showTeacherPicker = ref(false);
const showCoursePicker = ref(false);
const teacherList = ref([]);
const selectedDate = ref([]);
const courseList = ref([]);
const startTimeDefault = ref(['12', '00']);
const endTimeDefault = ref(['13', '00']);
const teacherSearchQuery = ref('');
const filteredTeacherList = ref([]);
const conflictVisible = ref(false);
const conflictData = ref([]);
const conflictMessage = ref('');

function getCourseList() {
    getScheduleCourseList({
        courseId: courseId.value
    }).then(response => {
        courseList.value = response;
    });
}

function getTeachers() {
    getTeacherList().then(response => {
        teacherList.value = response;
        filteredTeacherList.value = response;
    });
}

function getSchedulePlan() {
    getAttendanceDetailByPlanIdForH5(schedulePlanId.value).then(response => {
        Object.assign(attendanceDetail, response);
        attendanceDetail.classDate = attendanceDetail.startDateTime?.split(" ")[0] || '';

        if (response.startDateTime) {
            const startTime = response.startDateTime.split(" ")[1].slice(0, -3);
            attendanceDetail.startTime = startTime;
            startTimeDefault.value = startTime.split(':');
        } else {
            startTimeDefault.value = ['12', '00'];
        }

        if (response.endDateTime) {
            const endTime = response.endDateTime.split(" ")[1].slice(0, -3);
            attendanceDetail.endTime = endTime;
            endTimeDefault.value = endTime.split(':');
        } else {
            endTimeDefault.value = ['13', '00'];
        }

        attendanceDetail.isTransCampus = Boolean(response.isTransCampus || false);
        attendanceDetail.overClassHour = Number(response.overClassHour || 0);
    });
}

function showDatePickers() {
    selectedDate.value = attendanceDetail.classDate.split('-');
    showDatePicker.value = true;
}

function onConfirmDate({ selectedValues }) {
    attendanceDetail.classDate = `${selectedValues[0]}-${selectedValues[1]}-${selectedValues[2]}`;
    showDatePicker.value = false;
}

function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function onStartTimeConfirm(time) {
    attendanceDetail.startTime = `${time.selectedValues[0]}:${time.selectedValues[1]}`;
    showStartTimePicker.value = false;
}

function onEndTimeConfirm(time) {
    attendanceDetail.endTime = `${time.selectedValues[0]}:${time.selectedValues[1]}`;
    showEndTimePicker.value = false;
}

function onCourseConfirm({ selectedOptions }) {
    const course = selectedOptions[0];
    if (course) {
        attendanceDetail.courseName = course.courseName;
        courseId.value = course.courseId;
    }
    showCoursePicker.value = false;
}

function filterTeachers() {
    const query = teacherSearchQuery.value;
    filteredTeacherList.value = teacherList.value.filter(teacher =>
        teacher.nickName.includes(query)
    );
}

function selectTeacher(teacher) {
    attendanceDetail.teacherName = teacher.nickName;
    attendanceDetail.teacherId = teacher.userId;
    showTeacherPicker.value = false;
}

function convertTimeToMinutes(timeStr) {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + (minutes || 0);
}

function onSubmit() {
    const startTime = attendanceDetail.startTime;
    const endTime = attendanceDetail.endTime;

    const startMinutes = convertTimeToMinutes(startTime);
    const endMinutes = convertTimeToMinutes(endTime);

    if (endMinutes <= startMinutes) {
        showToast('上课结束时间必须大于开始时间');
        return;
    }

    loading.value = true;
    const param = {
        classDate: attendanceDetail.classDate,
        scheduleId: attendanceDetail.scheduleId,
        schedulePlanId: schedulePlanId.value,
        classTime: [attendanceDetail.startTime, attendanceDetail.endTime],
        teacherId: attendanceDetail.teacherId,
        courseId: courseId.value,
        isTransCampus: attendanceDetail.isTransCampus ? 1 : 0,
        overClassHour: attendanceDetail.overClassHour,
        updateMethod: 1,
        classContent: attendanceDetail.classContent,
    };

    updateSchedulePlan(param).then(() => {
        showToast('修改成功');
        router.back();
    }).catch(err => {
        if (err && err.data && Array.isArray(err.data) && err.data.length > 0) {
            conflictData.value = err.data;
            conflictMessage.value = err.msg || '存在排课时间冲突，冲突详情如下：';
            conflictVisible.value = true;
        }
    }).finally(() => {
        loading.value = false;
    });
}

onMounted(() => {
    schedulePlanId.value = route.query.schedulePlanId;
    courseId.value = route.query.courseId;
    getSchedulePlan();
    getCourseList();
    getTeachers();
});
</script>

<style scoped>
/* 弹窗内容区域 */
.van-popup--bottom {
    background-color: #fff;
}

/* 搜索框样式 */
.van-search {
    padding: 10px 16px;
}

/* 列表项样式 */
.van-cell {
    border-bottom: 1px solid #ebedf0;
}

.loading-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
</style>
